/* site/siteLang.js — the public site's language switch (EN · IT · DE · FR).
 *
 * The prototype ships a 155 KB inline script that translates the rendered page at runtime: the
 * English source string IS the key, every text node and a handful of attributes are swapped, and a
 * MutationObserver keeps late-rendered content translated. This is that behaviour, ported — with the
 * dictionaries split per language (i18n/<lang>.json, ~51 KB each) and DYNAMICALLY imported, so a
 * visitor reading the site in English never downloads them.
 *
 * Scope: the SITE ROOT only. The platform's own translation engine (src/i18n) owns the signed-in
 * apps; this never touches them, and it never writes the platform's locale.
 *
 * React interop: the original English is remembered per node, so switching back to EN restores it
 * exactly, and when React re-renders a node with new English text we re-key off that new value.
 * Our own writes are ignored by the observer via a re-entrancy guard.
 */
export const SITE_LANGS = ["en", "it", "de", "fr"];
const STORE_KEY = "lhcLang";
const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const SKIP_PARENTS = /^(SCRIPT|STYLE|NOSCRIPT|TEXTAREA)$/;

const dicts = {};                 // lang -> dictionary (memoised)
const originalText = new WeakMap();   // text node -> its English value
const originalAttr = new WeakMap();   // element -> { attr: english }

export function getStoredLang() {
  try {
    const v = localStorage.getItem(STORE_KEY);
    if (v && SITE_LANGS.indexOf(v) > -1) return v;
  } catch (e) { /* private mode */ }
  return "en";
}

export function storeLang(lang) {
  try { localStorage.setItem(STORE_KEY, lang); } catch (e) { /* private mode */ }
}

/** Load (once) the dictionary for a language. English needs none. */
export async function loadDict(lang) {
  if (lang === "en") return null;
  if (dicts[lang]) return dicts[lang];
  const mod = await (lang === "it" ? import("./i18n/it.json")
    : lang === "de" ? import("./i18n/de.json")
      : lang === "fr" ? import("./i18n/fr.json") : Promise.resolve(null));
  dicts[lang] = (mod && (mod.default || mod)) || null;
  return dicts[lang];
}

const collapse = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();
const translate = (dict, en) => (dict ? dict[collapse(en)] || null : null);

function applyTextNode(node, dict) {
  const parent = node.parentElement;
  if (!parent || SKIP_PARENTS.test(parent.tagName)) return;
  let en = originalText.get(node);
  if (en === undefined) {
    if (!collapse(node.nodeValue)) return;         // whitespace-only: nothing to translate
    en = node.nodeValue;
    originalText.set(node, en);
  }
  let out = en;
  if (dict) {
    const hit = translate(dict, en);
    if (hit !== null) {
      const m = en.match(/^(\s*)[\s\S]*?(\s*)$/);   // keep the node's own leading/trailing space
      out = m[1] + hit + m[2];
    }
  }
  if (node.nodeValue !== out) node.nodeValue = out;
}

function applyAttrs(el, dict) {
  if (!el.hasAttribute) return;
  let store = originalAttr.get(el);
  ATTRS.forEach((at) => {
    if (!el.hasAttribute(at)) return;
    if (!store) { store = {}; originalAttr.set(el, store); }
    if (store[at] === undefined) store[at] = el.getAttribute(at);
    const en = store[at];
    const out = dict ? (translate(dict, en) || en) : en;
    if (el.getAttribute(at) !== out) el.setAttribute(at, out);
  });
}

/** Translate a subtree in place (dict === null restores English). */
export function applyLang(root, dict) {
  if (!root) return;
  if (root.nodeType === 3) { applyTextNode(root, dict); return; }
  if (root.nodeType !== 1) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes = [];
  let n = walker.nextNode();
  while (n) { nodes.push(n); n = walker.nextNode(); }
  nodes.forEach((t) => applyTextNode(t, dict));
  applyAttrs(root, dict);
  root.querySelectorAll("[" + ATTRS.join("],[") + "]").forEach((el) => applyAttrs(el, dict));
}

/**
 * Keep `root` translated as it changes. Returns a disposer.
 * `childList` catches newly rendered content; `characterData` catches React rewriting an existing
 * node (a result count, a confirmation line) — in which case the new value becomes the new English
 * source. A re-entrancy guard stops our own writes from feeding back.
 */
export function watchLang(root, dict) {
  if (!root || typeof MutationObserver === "undefined") return () => {};
  let busy = false;
  const run = (fn) => { busy = true; try { fn(); } finally { busy = false; } };
  run(() => applyLang(root, dict));
  const mo = new MutationObserver((muts) => {
    if (busy) return;
    run(() => {
      muts.forEach((m) => {
        if (m.type === "characterData") {
          originalText.set(m.target, m.target.nodeValue);   // React re-keyed this node
          applyTextNode(m.target, dict);
          return;
        }
        m.addedNodes.forEach((node) => applyLang(node, dict));
        if (m.type === "attributes" && m.target) applyAttrs(m.target, dict);
      });
    });
  });
  mo.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  return () => mo.disconnect();
}

/** test seam */
export function __resetDicts() { Object.keys(dicts).forEach((k) => delete dicts[k]); }
